///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { PlannerEulaDTO } from '../generated/shared-types.model';
import { UserAuthorities } from '../services/user/user-authorities';
import { combineLatestWith, filter, map } from 'rxjs/operators';
import {
    selectCurrentAccount,
    selectPageNumber,
    selectShowEditButtonPermission,
    selectTotalItems,
    selectUsersList
} from './store/users-list.selectors';
import { Store } from '@ngrx/store';
import { getCurrentAccount, getShowEditButton, getUsersPage, loadPage } from './store/users-list.actions';
import { getEula } from '../shared/store/eula/eula.actions';
import { selectQueryParams, selectRouteData } from '../router-store/router.selector';
import { DialogAction } from '../dialog-manager/dialog-action-enum';
import { Observable } from 'rxjs';
import { SuiUserDTO } from '../generated/shared-sui-types.model';
import { UpdatedSuiUserDTO } from './models/updated-sui-user-dto';
import { Params } from '@angular/router';
import { CUSTOMERS_BASE_URL } from '../customers-config/customers.route';
import { selectEula } from '../shared/store/eula/eula.selectors';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-users-grid',
    templateUrl: './users-grid.component.html',
    styleUrls: ['users-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UsersGridComponent implements OnInit {
    public currentEULA$: Observable<PlannerEulaDTO> = this.store.select(selectEula);
    public currentAccount$: Observable<SuiUserDTO> = this.store.select(selectCurrentAccount);
    public showEditBtn$: Observable<boolean> = this.store.select(selectShowEditButtonPermission);
    public baseUrl$: Observable<string> = this.store.select(selectRouteData).pipe(map((routeData) => routeData.tabName));

    public customerFilter$: Observable<string> = this.store
        .select(selectQueryParams)
        .pipe(map((queryParams: Params) => queryParams.filter));

    public users$: Observable<UpdatedSuiUserDTO[]> = this.store.select(selectUsersList).pipe(
        combineLatestWith(this.customerFilter$),
        map(([customers, filter]): UpdatedSuiUserDTO[] => (customers ? this.handleCustomers(customers, filter) : []))
    );
    public totalItems$: Observable<number> = this.store.select(selectTotalItems);
    public page$: Observable<number> = this.store.select(selectPageNumber);
    public userAuthorities = UserAuthorities;
    public isShowEULACol$: Observable<boolean> = this.currentAccount$.pipe(
        combineLatestWith(this.baseUrl$),
        filter(([currentAccount]) => isDefined(currentAccount)),
        map(
            ([currentAccount, tabName]) =>
                this.userAuthorities.ALL_SIKLU.includes(currentAccount.authorities[0].value) && tabName === CUSTOMERS_BASE_URL
        )
    );
    public action = DialogAction;

    constructor(private readonly store: Store) {}

    ngOnInit(): void {
        this.store.dispatch(getUsersPage());
        this.store.dispatch(getShowEditButton());
        this.store.dispatch(getCurrentAccount());
        this.store.dispatch(getEula());
    }

    trackIdentity(_index, item: SuiUserDTO): number {
        return item.id.value;
    }

    loadPage(page: number): void {
        this.store.dispatch(loadPage({ page }));
    }

    //below methods only for customers tab

    private handleCustomers(customers: SuiUserDTO[], customerFilter: string): UpdatedSuiUserDTO[] {
        return customers.map(
            (user: SuiUserDTO) => new UpdatedSuiUserDTO(user.toDto(), customerFilter && user.login.value.includes(customerFilter))
        );
    }
}
